<template>
  <div id="app">
    <div class="header-container">
      <!-- 在线人数和IP属地成组，天气卡片在右侧 -->
      <div class="online-info">
        <div class="status-group">
          <div class="online-count">
            <div class="online-count-left">
              <i class="fa-solid fa-users"></i>
              <span class="count-text">{{ onlineCount }}</span>
              <span class="count-label">在线</span>
            </div>
            <div class="connection-status">
              <i :class="isConnected ? 'fa-solid fa-circle connected' : 'fa-solid fa-circle disconnected'"></i>
              <span class="status-text">{{ isConnected ? '已连接' : '未连接' }}</span>
            </div>
          </div>
          <div class="user-location" v-if="userLocation.ip !== '未知IP'">
            <i class="fa-solid fa-location-dot"></i>
            <span class="location-text">{{ userLocation.region }}</span>
            <span class="isp-text">({{ userLocation.isp }})</span>
          </div>
        </div>

        <!-- 天气卡片与气泡详情 -->
        <el-popover
          v-if="weatherData"
          placement="bottom-start"
          :width="350"
          trigger="click"
          popper-class="weather-popover"
        >
          <template #reference>
            <div class="weather-card" :class="getWeatherEffectClass(weatherData.weather)">
              <!-- 景深背景粒子特效装饰 -->
              <div class="weather-bg-effect">
                <span v-for="n in 8" :key="n" class="effect-particle"></span>
              </div>
              <div class="weather-card-top">
                <i :class="getWeatherIconClass(weatherData.weather)" class="weather-icon-inline"></i>
                <span class="weather-text">{{ weatherData.weather }}</span>
                <span class="weather-temp">{{ weatherData.temperature }}°C</span>
              </div>
              <div class="weather-card-bottom" v-if="weatherData.windDirection || weatherData.windPower">
                <i class="fa-solid fa-wind weather-icon-wind"></i>
                <span class="weather-wind">
                  {{ weatherData.windDirection }} {{ weatherData.windPower }}
                </span>
              </div>
            </div>
          </template>
          
          <div class="weather-detail-panel" v-if="weatherData.detailedData">
            <!-- 头部：更新时间 -->
            <div class="pop-header" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; border-bottom: 1px dashed rgba(255,255,255,0.15); padding-bottom: 6px;">
              <div class="pop-location" v-if="weatherData.detailedData?.province || weatherData.detailedData?.city || weatherData.province || weatherData.city" style="color: rgba(255,255,255,0.7); font-size: 12px; font-weight: 500; display: flex; align-items: center; gap: 4px;">
                <i class="fa-solid fa-location-dot" style="font-size: 10px; color: #409EFF;"></i>
                {{ weatherData.detailedData?.province || weatherData.province }} {{ weatherData.detailedData?.city || weatherData.city }}
              </div>
              <div class="pop-time" v-if="weatherData.updatedAt" style="color: rgba(255,255,255,0.5); font-size: 11px;">
                更新于 {{ formatTime(weatherData.updatedAt) }}
              </div>
            </div>

            <!-- 主要指数网格 -->
            <div class="pop-main">
              <div class="pop-temp-box">
                <span class="pop-temp-num">{{ weatherData.detailedData.temperature }}</span>
                <span class="pop-temp-unit">°C</span>
                <span class="pop-weather-txt">{{ weatherData.detailedData.weather }}</span>
              </div>
              <div class="pop-sub-grid">
                <div class="sub-item" v-if="weatherData.detailedData.feels_like !== undefined">
                  <span class="sub-label">体感</span>
                  <span class="sub-val">{{ weatherData.detailedData.feels_like }}°C</span>
                </div>
                <div class="sub-item" v-if="weatherData.detailedData.humidity !== undefined">
                  <span class="sub-label">湿度</span>
                  <span class="sub-val">{{ weatherData.detailedData.humidity }}%</span>
                </div>
              </div>
            </div>

            <!-- AQI 空气质量 -->
            <div class="pop-aqi-box" v-if="weatherData.detailedData.aqi !== undefined">
              <div class="aqi-header">
                <span class="aqi-title">空气质量指数 (AQI)</span>
                <span :class="'aqi-badge level-' + (weatherData.detailedData.aqi <= 50 ? 1 : (weatherData.detailedData.aqi <= 100 ? 2 : (weatherData.detailedData.aqi <= 150 ? 3 : 4)))">
                  {{ weatherData.detailedData.aqi }} - {{ weatherData.detailedData.aqi_category || '优' }}
                </span>
              </div>
              <div class="aqi-pollutants-single" v-if="weatherData.detailedData.pm25 !== undefined">
                <span class="pm-label">PM2.5 核心细颗粒物浓度:</span>
                <span class="pm-val">{{ weatherData.detailedData.pm25 }} μg/m³</span>
              </div>
            </div>

            <!-- 预警信息展示 -->
            <div class="pop-alerts" v-if="weatherData.detailedData.alerts && weatherData.detailedData.alerts.length > 0">
              <div class="alert-item" v-for="(alert, idx) in weatherData.detailedData.alerts" :key="idx">
                <div class="alert-title">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  <span>{{ alert.title }}</span>
                </div>
                <div class="alert-text">{{ alert.text }}</div>
              </div>
            </div>

            <!-- 生活指数精选 -->
            <div class="pop-indices" v-if="weatherData.detailedData.life_indices">
              <div class="indices-list">
                <div class="index-item" v-if="weatherData.detailedData.life_indices.clothing">
                  <span class="index-icon">👕</span>
                  <div class="index-body">
                    <span class="index-name">穿衣指数: {{ weatherData.detailedData.life_indices.clothing.level }}</span>
                    <span class="index-desc">{{ weatherData.detailedData.life_indices.clothing.advice }}</span>
                  </div>
                </div>
                <div class="index-item" v-if="weatherData.detailedData.life_indices.uv">
                  <span class="index-icon">☀️</span>
                  <div class="index-body">
                    <span class="index-name">紫外线: {{ weatherData.detailedData.life_indices.uv.level }}</span>
                    <span class="index-desc">{{ weatherData.detailedData.life_indices.uv.advice }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="weather-detail-fallback" v-else>
            <p>暂无详细天气指标</p>
          </div>
        </el-popover>
       
      </div>
      
      <h1 class="main-title" @mouseenter="startHoverTimer" @mouseleave="clearHoverTimer">
        <span class="h1title" 
              :class="{ 'typing-caret': isTyping }"
              style="cursor: default;">
            {{ titleText }}
        </span>
      </h1>
      

      <div class="user-actions">
        <!-- 夜间模式切换按钮 -->
        <div class="theme-toggle-container">
          <a @click="blackmode()" class="theme-toggle-btn" :title="isDarkMode ? '切换到日间模式' : '切换到夜间模式'">
            <i :class="isDarkMode ? 'fa-solid fa-moon' : 'fa-solid fa-sun'"></i>
          </a>
        </div>
        
        <div v-if="!isLoggedIn" class="auth-buttons">
          <button @click="showLoginModal" class="auth-btn login-btn">登录</button>
          <button @click="showRegisterModal" class="auth-btn register-btn">注册</button>
        </div>
        <div v-else class="user-info">
          <div class="user-avatar" @click="toggleUserMenu">
            <img v-if="userAvatar" :src="userAvatar" :alt="userInfo.username" @error="onAvatarError" />
            <div v-else class="avatar-fallback-letter">{{ (userInfo.username || 'U').charAt(0).toUpperCase() }}</div>
          </div>
          <div class="user-menu" v-show="showUserMenu">
            <div class="user-name">{{ userInfo.nickname || userInfo.username }}</div>
            <router-link to="/myinfo" class="menu-item" @click="showUserMenu = false">
              <i class="fa-solid fa-user"></i>个人中心
            </router-link>
            <router-link to="/shop" class="menu-item" @click="showUserMenu = false">
              <i class="fa-solid fa-store"></i>个性化商城
            </router-link>
            <router-link to="/account" class="menu-item" @click="showUserMenu = false">
              <i class="fa-solid fa-shield-halved"></i>个人信息管理
            </router-link>
            <div class="menu-item" @click="logout">
              <i class="fa-solid fa-right-from-bracket"></i>退出登录
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted, h } from 'vue'
import { useRouter } from 'vue-router'
import { message } from '@/utils/ui-service'
import axios from 'axios'
import serverUrl, { avatarUrl } from '@/assets/BaseServerUrl.js'
import websocketService from '@/services/websocketService.js'
import authService from '@/services/authService.js'
import eventBus from '@/utils/eventBus.js'

export default {
  name: 'HomeHead',
  emits: ['show-login', 'show-register'],
  setup(props, { emit }) {
    const router = useRouter()
    const showUserMenu = ref(false)
    const userInfo = ref({})
    const isLoggedIn = ref(false)
    
    // 天气相关状态
    const weatherData = ref(null)
    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const m = (date.getMonth() + 1).toString().padStart(2, '0')
      const d = date.getDate().toString().padStart(2, '0')
      const h = date.getHours().toString().padStart(2, '0')
      const min = date.getMinutes().toString().padStart(2, '0')
      return `${m}-${d} ${h}:${min}`
    }
    // 中国 34 个省级行政区
    const DOMESTIC_REGIONS = [
      '北京', '天津', '河北', '山西', '内蒙古', '辽宁', '吉林', '黑龙江',
      '上海', '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南',
      '湖北', '湖南', '广东', '广西', '海南', '重庆', '四川', '贵州',
      '云南', '西藏', '陕西', '甘肃', '青海', '宁夏', '新疆', '台湾',
      '香港', '澳门'
    ]

    // 常见海外关键词
    const OVERSEAS_KEYWORDS = [
      '得克萨斯', '加利福尼亚', '华盛顿', '纽约', '伦敦', '东京', '首尔', '巴黎', '柏林',
      '美国', '英国', '日本', '韩国', '德国', '法国', '加拿大', '澳大利亚', '俄罗斯',
      '新加坡', '马来西亚', '泰国', '越南', '菲律宾', '印度', '印度尼西亚',
      '巴西', '阿根廷', '墨西哥', '荷兰', '意大利', '西班牙', '欧洲', '美洲', '海外', '国外'
    ]

    // 校验是否为国内合法区域
    const isDomesticRegion = (regionStr) => {
      if (!regionStr || typeof regionStr !== 'string') return false
      const reg = regionStr.trim()
      if (['未知地区', '未知属地', '内网IP', '未知IP', '局域网', '本机地址', '保留地址'].includes(reg)) {
        return false
      }
      if (/^[a-zA-Z\s,.-]+$/.test(reg)) {
        return false
      }
      for (const kw of OVERSEAS_KEYWORDS) {
        if (reg.includes(kw)) {
          return false
        }
      }
      return DOMESTIC_REGIONS.some(prov => reg.includes(prov))
    }

    const fetchWeatherData = async () => {
      try {
        const res = await axios.get(`${serverUrl}/api/weather`)
        if (res.data.success && res.data.data && !res.data.isOverseas && res.data.showWeather !== false) {
          weatherData.value = res.data.data
        } else {
          weatherData.value = null
        }
      } catch (err) {
        console.error('获取天气信息失败:', err.response?.data?.error || err.message)
        weatherData.value = null
      }
    }

    const weatherTooltip = computed(() => {
      if (!weatherData.value) return ''
      const { location, weather, temperature, windDirection, windPower } = weatherData.value
      return `${location}当前天气：${weather}\n温度：${temperature}°C\n风向：${windDirection}\n风力：${windPower}`
    })

    const getWeatherIconClass = (weatherText) => {
      if (!weatherText) return 'fa-solid fa-temperature-half'
      if (weatherText.includes('晴')) return 'fa-solid fa-sun'
      if (weatherText.includes('雨') || weatherText.includes('阵雨')) return 'fa-solid fa-cloud-showers-heavy'
      if (weatherText.includes('阴')) return 'fa-solid fa-cloud'
      if (weatherText.includes('云') || weatherText.includes('多云')) return 'fa-solid fa-cloud-sun'
      if (weatherText.includes('雪')) return 'fa-solid fa-snowflake'
      if (weatherText.includes('雾') || weatherText.includes('霾')) return 'fa-solid fa-smog'
      return 'fa-solid fa-temperature-half'
    }

    const getWeatherEffectClass = (weatherText) => {
      if (!weatherText) return 'weather-effect-default'
      if (weatherText.includes('雨') || weatherText.includes('阵雨') || weatherText.includes('雷')) {
        return 'weather-effect-rain'
      }
      if (weatherText.includes('雪') || weatherText.includes('雹')) {
        return 'weather-effect-snow'
      }
      if (weatherText.includes('晴')) {
        return 'weather-effect-sunny'
      }
      if (weatherText.includes('云') || weatherText.includes('多云')) {
        return 'weather-effect-cloudy'
      }
      if (weatherText.includes('雾') || weatherText.includes('霾') || weatherText.includes('沙')) {
        return 'weather-effect-haze'
      }
      return 'weather-effect-default'
    }

    // WebSocket相关状态
    const onlineCount = ref(0)
    const isConnected = ref(false)
    const userLocation = ref({
      ip: '未知IP',
      region: '未知地区',
      isp: '未知ISP'
    })
    
    // 欢迎信息相关
    const currentTimeGreeting = ref('')
    const referrerSource = ref('')
    
    // 标题悬停相关
    const titleText = ref('云语匀雨')
    const serverStartTime = new Date('2025-03-01 18:30:00') // 正式开服时间
    
    // 打字机与悬停稳定判断相关状态
    const isTyping = ref(false)
    let greetingInterval = null

  const isDisplayingUptime = ref(false)
    const isPointerOverTitle = ref(false)
    const hoverDelayTimeout = ref(null)
    const typingTimer = ref(null)
    const uptimeInterval = ref(null)
    // 防止重复触发的锁
    const hoverActive = ref(false)
    
    // 打字机效果
    const typeText = (targetText, speed = 40) => {
      return new Promise((resolve) => {
        isTyping.value = true
        clearInterval(typingTimer.value)
        let index = 1
        // 避免出现“仅光标无文字”的瞬间
        titleText.value = targetText.slice(0, 1)
        typingTimer.value = setInterval(() => {
          titleText.value = targetText.slice(0, index)
          index++
          if (index > targetText.length) {
            clearInterval(typingTimer.value)
            isTyping.value = false
            resolve()
          }
        }, speed)
      })
    }
    
    // 获取时间问候语
    const getTimeGreeting = () => {
      const hour = new Date().getHours()
      if (hour >= 0 && hour < 4) return '凌晨'
      if (hour >= 4 && hour < 7) return '早晨'
      if (hour >= 7 && hour < 9) return '上午'
      if (hour >= 9 && hour < 11) return '上午'
      if (hour >= 11 && hour < 13) return '正午'
      if (hour >= 13 && hour < 17) return '下午'
      if (hour >= 17 && hour < 19) return '黄昏'
      if (hour >= 19 && hour < 22) return '晚上'
      return '深夜'
    }
    
    // 获取来源信息
    const getReferrerSource = () => {
      const referrer = document.referrer
      if (!referrer) {
        return '直接访问'
      }
      
      try {
        const url = new URL(referrer)
        const hostname = url.hostname

        // 常见搜索引擎和网站映射
        const sourceMap = {
          'www.baidu.com': '百度搜索',
          'baidu.com': '百度搜索',
          'www.google.com': '谷歌搜索',
          'google.com': '谷歌搜索',
          'cn.bing.com': '必应搜索',
          'www.bing.com': '必应搜索',
          'bing.com': '必应搜索',
          'www.so.com': '360搜索',
          'so.com': '360搜索',
          'www.sogou.com': '搜狗搜索',
          'sogou.com': '搜狗搜索',
          'github.com': 'GitHub',
          'gitee.com': 'Gitee',
          'rainview.net': '语景小站',
          'butvan.cn': '可梵的个人博客',
          'weixin110.qq.com': '微信',
          '4yshop.top': '云语匀雨'
        }

        // 排除本站自身的跳转，将其视为直接访问
        if (hostname === window.location.hostname || hostname === '4yshop.top') {
          return '直接访问'
        }

        // OAuth 回调跳转不算外部来源（QQ、微信等登录后返回）
        const oauthHosts = [
          'graph.qq.com',          // QQ 登录
          'open.weixin.qq.com',    // 微信网页授权
          'api.weixin.qq.com',     // 微信 API
          'accounts.google.com',   // Google 登录
          'github.com'             // GitHub OAuth（已在 sourceMap 中，不会走到这里）
        ]
        if (oauthHosts.includes(hostname)) {
          return '直接访问'
        }

        return sourceMap[hostname] || hostname
      } catch (error) {
        return '直接访问'
      }
    }
    
    // 更新时间问候语
    const updateTimeGreeting = () => {
      currentTimeGreeting.value = getTimeGreeting()
    }
    
    // 显示欢迎通知
    const showWelcomeNotification = (onClose) => {
      let welcomeMsg
      const greeting = currentTimeGreeting.value
      const region = userLocation.value.region !== '未知地区' ? userLocation.value.region : ''
      
      if (!isLoggedIn.value) {
        // 游客访问
        if (referrerSource.value === '直接访问') {
          welcomeMsg = h('div', {}, [
            '欢迎来到云语匀雨的',
            h('span', { style: 'color: #409EFF; font-weight: bold;' }, region ? `${region}的小伙伴` : '新朋友'),
            '，',
            h('span', { style: 'color: #67C23A; font-weight: bold;' }, greeting),
            '好！'
          ])
        } else {
          welcomeMsg = h('div', {}, [
            '欢迎从',
            h('span', { style: 'color: #409EFF; font-weight: bold;' }, referrerSource.value),
            '来到云语匀雨的',
            h('span', { style: 'color: #409EFF; font-weight: bold;' }, region ? `${region}小伙伴` : '朋友'),
            '，',
            h('span', { style: 'color: #67C23A; font-weight: bold;' }, greeting),
            '好！'
          ])
        }
      } else {
        const nickname = userInfo.value.nickname || userInfo.value.username
        
        // 对于已登录用户，除非是从外部知名网站跳转，否则统一使用简洁欢迎语
        const isExternal = referrerSource.value !== '直接访问' && !['未知来源'].includes(referrerSource.value)
        
        if (!isExternal) {
          welcomeMsg = h('div', {}, [
            h('span', { style: 'color: #409EFF; font-weight: bold;' }, nickname),
            `，${greeting}好！`,
            h('span', { style: 'color: #67C23A; font-weight: bold; margin-left: 5px;' }, '欢迎回来')
          ])
        } else {
          welcomeMsg = h('div', {}, [
            h('span', { style: 'color: #409EFF; font-weight: bold;' }, nickname),
            '，',
            h('span', { style: 'color: #67C23A; font-weight: bold;' }, greeting),
            '好！欢迎从',
            h('span', { style: 'color: #409EFF; font-weight: bold;' }, referrerSource.value),
            '回到这里'
          ])
        }
      }
      
      message({
        title: '欢迎信息',
        message: welcomeMsg,
        duration: 4000,
        onClose: () => {
          if (typeof onClose === 'function') {
            setTimeout(() => onClose(), 300)
          }
        }
      })
    }
    
    // 获取一言
    const fetchHitokoto = async (onClose) => {
      try {
        // 先获取一言配置
        const configResp = await axios.get(`${serverUrl}/api/hitokoto/config`)
        const config = configResp.data.data
        
        if (!config || !config.enabled) {
          if (typeof onClose === 'function') onClose()
          return
        }

        let hitokotoData;
        try {
          const response = await axios.get(config.apiUrl || 'https://v1.hitokoto.cn/?c=d&c=e&c=i&c=j&c=k', { timeout: 3000 })
          hitokotoData = response.data
        } catch (apiErr) {
          console.warn('Hitokoto API 请求失败，使用本地回退内容')
          hitokotoData = {
            hitokoto: '生活原本沉闷，但跑起来就会有风。',
            from: '励志'
          }
        }
        
        // 使用ElNotification展示一言
        message({
          title: '一言',
          message: h('div', {}, [
            h('div', { style: 'margin-bottom: 8px; font-size: 14px; line-height: 1.4;' }, hitokotoData.hitokoto),
            h('div', { style: 'font-size: 12px; color: #909399; text-align: right;' }, `—— ${hitokotoData.from || '未知'}`)
          ]),
          duration: 5000,
          onClose: () => {
            if (typeof onClose === 'function') {
              setTimeout(() => onClose(), 300)
            }
          }
        })
      } catch (error) {
        console.error('获取一言失败:', error)
        if (typeof onClose === 'function') onClose()
      }
    }

    // 显示每日问候（AI生成）
    const showHolidayNotification = async () => {
      try {
        const response = await axios.get(`${serverUrl}/api/daily-greeting`)
        if (response.data && response.data.success && response.data.data) {
          const { title, messages, tone } = response.data.data
          // 映射 tone 到 element-plus type
          let type = 'info'
          if (tone === 'celebratory') type = 'success'
          else if (tone === 'solemn') type = 'warning'
          else type = 'info'

          // 如果是数组则直接显示，如果是 string 则转数组
          const msgList = Array.isArray(messages) ? messages : [messages]

          if (msgList.length > 0) {
            message({
              title: title || '美好的一天',
              message: h('div', {}, msgList.map(m => h('div', { style: 'margin-bottom: 6px; font-size: 14px;' }, m))),
              duration: 6000,
              type
            })
          }
        }
        // 当 success 为 false (功能已禁用) 时，不再执行 showLocalHolidayNotification()，直接保持静默
      } catch (e) {
        console.error('获取AI每日问候失败:', e)
        // 发生错误时同样保持静默，不触发本地兜底
      }
    }

    // 计算开服时间
    const getServerUptime = () => {
      const now = new Date()
      const diff = now - serverStartTime
      
      if (diff < 0) {
        return '服务器尚未开服'
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)
      
      return `已正式开服 ${days} 天 ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    
    // 进入悬停：稳定判断 + 打字机切换到上线时长
    const startHoverTimer = () => {
      isPointerOverTitle.value = true
      // 已经在悬停展示状态时不再重复触发
      if (hoverActive.value) return
      // 防抖：必须稳定悬停一定时间才触发
      clearTimeout(hoverDelayTimeout.value)
      hoverDelayTimeout.value = setTimeout(async () => {
        if (!isPointerOverTitle.value || hoverActive.value) return
        // 加锁，避免重复进入
        hoverActive.value = true
        // 切换到上线时长（一次性打字机），后续仅文本更新避免抖动
        await typeText(getServerUptime(), 80)
        isDisplayingUptime.value = true
        clearInterval(uptimeInterval.value)
        uptimeInterval.value = setInterval(() => {
          if (!isPointerOverTitle.value || !isDisplayingUptime.value) return
          titleText.value = getServerUptime()
        }, 1000)
      }, 250)
    }
    
    // 离开悬停：停止更新时间并打字机切回主标题
    const clearHoverTimer = () => {
      isPointerOverTitle.value = false
      clearTimeout(hoverDelayTimeout.value)
      clearInterval(uptimeInterval.value)

      // 若当前正在显示上线时长，则平滑打字机回到主标题
      if (isDisplayingUptime.value) {
        const doRevert = async () => {
          await typeText('云语匀雨', 90)
          isDisplayingUptime.value = false
          // 解锁，允许下一次悬停再次触发
          hoverActive.value = false
        }
        if (isTyping.value) {
          // 等当前打字完成后再回退，避免抽搐
          const wait = setInterval(() => {
            if (!isTyping.value) {
              clearInterval(wait)
              doRevert()
            }
          }, 60)
        } else {
          doRevert()
        }
      } else {
        // 已是主标题，确保不显示光标
        isTyping.value = false
        clearInterval(typingTimer.value)
        titleText.value = '云语匀雨'
        hoverActive.value = false
      }
    }
    
    // 默认头像
    const avatarLoadFailed = ref(false)
    // 计算用户头像
    const userAvatar = computed(() => {
      if (avatarLoadFailed.value) return ''
      const av = userInfo.value.avatar
      if (av && av !== 'null' && av !== 'undefined' && av.trim() !== '') {
        if (av.startsWith('http')) {
          return av
        }
        return `${avatarUrl}/avatar/${av}`
      }
      return ''
    })

    // 头像加载失败时回退到默认头像
    const onAvatarError = () => {
      avatarLoadFailed.value = true
    }

    // 切换用户菜单
    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value
    }
    
    // 检查登录状态
    const checkLoginStatus = () => {
      const loginStatus = localStorage.getItem('isLoggedIn')
      const userData = localStorage.getItem('userInfo')
      
      if (loginStatus === 'true' && userData) {
        isLoggedIn.value = true
        userInfo.value = JSON.parse(userData)
      } else {
        isLoggedIn.value = false
        userInfo.value = {}
      }
      avatarLoadFailed.value = false
    }
    
    // 退出登录
    // 退出登录
    const logout = () => {
      authService.clearLocalAuth()
      isLoggedIn.value = false
      userInfo.value = {}
      showUserMenu.value = false
      router.push('/')
      message.success('已退出登录')
    }
    
    // 显示登录模态框
    const showLoginModal = () => {
      emit('show-login')
    }
    
    // 显示注册模态框
    const showRegisterModal = () => {
      emit('show-register')
    }
    
    // 夜间模式相关
    const isDarkMode = ref(false)
    
    const blackmode = () => {
      const body = document.body
      const isDark = body.classList.contains('dark-theme')
      
      if (isDark) {
        body.classList.remove('dark-theme')
        document.documentElement.classList.remove('dark-theme')
        localStorage.setItem('theme', 'light')
        isDarkMode.value = false
      } else {
        body.classList.add('dark-theme')
        document.documentElement.classList.add('dark-theme')
        localStorage.setItem('theme', 'dark')
        isDarkMode.value = true
      }
    }
    
    // 点击外部关闭菜单
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-info')) {
        showUserMenu.value = false
      }
    }
    
    // WebSocket回调函数
    const handleOnlineCount = (count) => {
      onlineCount.value = count
    }
    
    const handleUserInfo = (info) => {
      userLocation.value = info
      const reg = info?.region || '';
      // 只有国内合法省份/地区才调用天气接口并展示天气卡片；海外IP或未知IP不调用且隐藏卡片
      if (isDomesticRegion(reg)) {
        fetchWeatherData()
      } else {
        weatherData.value = null
      }
    }
    
    const handleConnected = () => {
      isConnected.value = true
    }
    
    const handleDisconnected = () => {
      isConnected.value = false
    }
    
    onMounted(() => {
      checkLoginStatus()
      document.addEventListener('click', handleClickOutside)
      
      // 初始化主题
      const savedTheme = localStorage.getItem('theme')
      if (savedTheme === 'dark') {
        document.body.classList.add('dark-theme')
        document.documentElement.classList.add('dark-theme')
        isDarkMode.value = true
      } else {
        document.documentElement.classList.remove('dark-theme')
        isDarkMode.value = false
      }
      
      // 监听存储变化
      window.addEventListener('storage', checkLoginStatus)
      
      // 监听路由变化，确保登录后立即更新状态
      router.afterEach(() => {
        checkLoginStatus()
      })
      
      // 初始化WebSocket连接
      websocketService.on('onlineCount', handleOnlineCount)
      websocketService.on('userInfo', handleUserInfo)
      websocketService.on('connected', handleConnected)
      websocketService.on('connected', handleConnected)
      websocketService.on('disconnected', handleDisconnected)
      
      // 监听全局登录状态检查事件
      eventBus.on('check-login-status', checkLoginStatus)
      // 监听认证清除事件（token 过期/自动退出），即时刷新头部登录态
      eventBus.on('auth-cleared', checkLoginStatus)
      
      // 连接WebSocket
      websocketService.connect()
      
      // 设置初始状态
      onlineCount.value = websocketService.getOnlineCount()
      userLocation.value = websocketService.getUserInfo()
      isConnected.value = websocketService.isSocketConnected()
      
      // 初始化欢迎信息
      updateTimeGreeting()
      referrerSource.value = getReferrerSource()
      
      const startWelcomeSequence = () => {
        showWelcomeNotification(() => fetchHitokoto(() => showHolidayNotification()))
      }

      let announcementShowing = false
      let welcomeStarted = false
      const tryStartWelcome = () => {
        if (welcomeStarted || announcementShowing) return
        welcomeStarted = true
        startWelcomeSequence()
      }

      eventBus.on('announcement-opened', () => { announcementShowing = true })
      eventBus.on('announcement-closed', () => {
        announcementShowing = false
        setTimeout(tryStartWelcome, 300)
      })
      eventBus.on('announcement-check-done', () => {
        setTimeout(tryStartWelcome, 300)
      })

      setTimeout(() => {
        tryStartWelcome()
      }, 3000)
      
      // 每分钟更新一次时间问候语
      greetingInterval = setInterval(updateTimeGreeting, 60000)
    })
    
    onUnmounted(() => {
      // 清理WebSocket监听器
      websocketService.off('onlineCount', handleOnlineCount)
      websocketService.off('userInfo', handleUserInfo)
      websocketService.off('connected', handleConnected)
      websocketService.off('disconnected', handleDisconnected)

      eventBus.off('check-login-status', checkLoginStatus)
      eventBus.off('auth-cleared', checkLoginStatus)
      eventBus.off('announcement-opened')
      eventBus.off('announcement-closed')
      eventBus.off('announcement-check-done')

      // 清理标题悬停定时器
      clearHoverTimer()
      // 清理问候时间更新定时器
      if (greetingInterval) clearInterval(greetingInterval)
    })
    
    return {
      showUserMenu,
      userInfo,
      isLoggedIn,
      userAvatar,
      onAvatarError,
      toggleUserMenu,
      logout,
      isDarkMode,
      blackmode,
      showLoginModal,
      showRegisterModal,
      onlineCount,
      isConnected,
      userLocation,
      currentTimeGreeting,
      referrerSource,
      titleText,
      startHoverTimer,
      clearHoverTimer,
      isTyping,
      weatherData,
      formatTime,
      weatherTooltip,
      getWeatherIconClass,
      getWeatherEffectClass
    }
  }
}
</script>

<style scoped>
.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.15); /* 提升一点不透明度替代 saturate */
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 24px;
  margin: 20px;
  padding: 25px 35px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  /* 移除 transition: all 以稳定蒙层表现 */
  transition: background 0.6s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.6s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: var(--z-modal);
}

/* 在线信息样式 */
.online-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  font-family: '爱点风雅黑', sans-serif;
  font-size: 14px;
  color: #333;
}

/* 在线人数与IP属地成组容器（扁卡片） */
.status-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  min-height: 52px;
  height: auto;
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.status-group:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.3);
}

body.dark-theme .status-group {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

body.dark-theme .status-group:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* 天气卡片样式（纵向两行及景深微光玻璃卡片） */
.weather-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 12px;
  cursor: pointer;
  box-sizing: border-box;
  min-height: 52px;
  height: auto;
  min-width: 130px;
  user-select: none;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  /* 默认兜底 */
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  color: #1d1d1f;
}

body.dark-theme .weather-card {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
}

.weather-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.weather-card-top {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  z-index: 2;
}

.weather-card-bottom {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: normal;
  opacity: 0.85;
  line-height: 1.2;
  z-index: 2;
}

.weather-icon-inline {
  font-size: 13px;
}

.weather-icon-wind {
  font-size: 10px;
  animation: wind-spin 6s infinite linear;
}

@keyframes wind-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ================== 天气特效景深体系 ================== */
.weather-bg-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

/* 1. 晴天景深 (Glow & Pulse) */
.weather-card.weather-effect-sunny {
  background: linear-gradient(135deg, rgba(255, 220, 0, 0.22), rgba(255, 120, 0, 0.18)) !important;
  border: 1px solid rgba(255, 200, 0, 0.38) !important;
  box-shadow: 0 4px 16px rgba(255, 120, 0, 0.1), inset 0 0 8px rgba(255, 220, 0, 0.15);
  color: #d97706;
}
body.dark-theme .weather-card.weather-effect-sunny {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 140, 0, 0.12)) !important;
  border-color: rgba(255, 215, 0, 0.25) !important;
  color: #fbbf24;
}
.weather-card.weather-effect-sunny .weather-bg-effect {
  background: radial-gradient(circle at 85% 20%, rgba(255, 220, 0, 0.45) 0%, rgba(255, 120, 0, 0) 65%);
  filter: blur(4px);
  animation: sunny-glow 5s infinite ease-in-out;
}
@keyframes sunny-glow {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 0.9; transform: scale(1.15); }
}

/* 2. 雨天景深 (斜向雨丝粒子) */
.weather-card.weather-effect-rain {
  background: linear-gradient(135deg, rgba(30, 80, 150, 0.22), rgba(70, 130, 180, 0.16)) !important;
  border: 1px solid rgba(30, 144, 255, 0.35) !important;
  box-shadow: 0 4px 16px rgba(16, 78, 139, 0.08), inset 0 0 8px rgba(70, 130, 180, 0.2);
  color: #1e60b5;
}
body.dark-theme .weather-card.weather-effect-rain {
  background: linear-gradient(135deg, rgba(16, 60, 120, 0.2), rgba(30, 130, 220, 0.12)) !important;
  border-color: rgba(30, 144, 255, 0.25) !important;
  color: #60a5fa;
}
.weather-card.weather-effect-rain .effect-particle {
  position: absolute;
  width: 1px;
  height: 6px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.7));
  animation: falling-rain 1.3s linear infinite;
  opacity: 0;
}
.weather-card.weather-effect-rain .effect-particle:nth-child(1) { left: 8%; animation-delay: 0.1s; animation-duration: 1.1s; }
.weather-card.weather-effect-rain .effect-particle:nth-child(2) { left: 23%; animation-delay: 0.4s; animation-duration: 1.4s; }
.weather-card.weather-effect-rain .effect-particle:nth-child(3) { left: 38%; animation-delay: 0.2s; animation-duration: 1.2s; }
.weather-card.weather-effect-rain .effect-particle:nth-child(4) { left: 53%; animation-delay: 0.6s; animation-duration: 1.5s; }
.weather-card.weather-effect-rain .effect-particle:nth-child(5) { left: 68%; animation-delay: 0.3s; animation-duration: 1.0s; }
.weather-card.weather-effect-rain .effect-particle:nth-child(6) { left: 83%; animation-delay: 0.7s; animation-duration: 1.3s; }
.weather-card.weather-effect-rain .effect-particle:nth-child(7) { left: 93%; animation-delay: 0.5s; animation-duration: 1.1s; }
.weather-card.weather-effect-rain .effect-particle:nth-child(8) { left: 48%; animation-delay: 0.8s; animation-duration: 1.4s; }

@keyframes falling-rain {
  0% { transform: translateY(-10px) translateX(-5px) rotate(15deg); opacity: 0; }
  30% { opacity: 0.85; }
  90% { opacity: 0.85; }
  100% { transform: translateY(50px) translateX(6px) rotate(15deg); opacity: 0; }
}

/* 3. 雪天景深 (旋转飘落雪花粒子) */
.weather-card.weather-effect-snow {
  background: linear-gradient(135deg, rgba(210, 230, 235, 0.28), rgba(160, 220, 230, 0.18)) !important;
  border: 1px solid rgba(160, 220, 230, 0.38) !important;
  box-shadow: 0 4px 16px rgba(160, 220, 230, 0.08), inset 0 0 8px rgba(255, 255, 255, 0.25);
  color: #4b8285;
}
body.dark-theme .weather-card.weather-effect-snow {
  background: linear-gradient(135deg, rgba(200, 220, 225, 0.16), rgba(100, 150, 160, 0.12)) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
  color: #cbd5e1;
}
.weather-card.weather-effect-snow .effect-particle {
  position: absolute;
  width: 3px;
  height: 3px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  animation: falling-snow 3.8s linear infinite;
  filter: blur(0.3px);
  opacity: 0;
}
.weather-card.weather-effect-snow .effect-particle:nth-child(1) { left: 12%; animation-delay: 0.2s; animation-duration: 3.2s; width: 2.5px; height: 2.5px; }
.weather-card.weather-effect-snow .effect-particle:nth-child(2) { left: 28%; animation-delay: 1.4s; animation-duration: 4.2s; width: 4px; height: 4px; }
.weather-card.weather-effect-snow .effect-particle:nth-child(3) { left: 42%; animation-delay: 0.7s; animation-duration: 3.5s; width: 2px; height: 2px; }
.weather-card.weather-effect-snow .effect-particle:nth-child(4) { left: 58%; animation-delay: 2.1s; animation-duration: 4.6s; width: 4.5px; height: 4.5px; }
.weather-card.weather-effect-snow .effect-particle:nth-child(5) { left: 72%; animation-delay: 0.4s; animation-duration: 3.0s; width: 3px; height: 3px; }
.weather-card.weather-effect-snow .effect-particle:nth-child(6) { left: 88%; animation-delay: 1.8s; animation-duration: 4.0s; width: 2px; height: 2px; }
.weather-card.weather-effect-snow .effect-particle:nth-child(7) { left: 6%; animation-delay: 2.6s; animation-duration: 4.5s; width: 4px; height: 4px; }
.weather-card.weather-effect-snow .effect-particle:nth-child(8) { left: 50%; animation-delay: 0.1s; animation-duration: 3.3s; width: 3px; height: 3px; }

@keyframes falling-snow {
  0% { transform: translateY(-8px) translateX(0); opacity: 0; }
  10% { opacity: 0.85; }
  90% { opacity: 0.85; }
  100% { transform: translateY(50px) translateX(12px); opacity: 0; }
}

/* 4. 多云/阴天景深 (Drifting Cloud Mask) */
.weather-card.weather-effect-cloudy {
  background: linear-gradient(135deg, rgba(165, 185, 205, 0.22), rgba(110, 125, 140, 0.16)) !important;
  border: 1px solid rgba(165, 185, 205, 0.35) !important;
  box-shadow: 0 4px 16px rgba(110, 125, 140, 0.06);
  color: #576574;
}
body.dark-theme .weather-card.weather-effect-cloudy {
  background: linear-gradient(135deg, rgba(70, 80, 95, 0.22), rgba(45, 55, 65, 0.15)) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: #94a3b8;
}
.weather-card.weather-effect-cloudy .weather-bg-effect {
  background: radial-gradient(ellipse at 30% 30%, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 65%);
  animation: cloudy-drift 10s infinite linear;
}
@keyframes cloudy-drift {
  0%, 100% { transform: scale(1) translateX(0); }
  50% { transform: scale(1.15) translateX(10%); }
}

/* 5. 雾霾天景深 (Haze Fog Drift) */
.weather-card.weather-effect-haze {
  background: linear-gradient(135deg, rgba(200, 175, 140, 0.22), rgba(170, 135, 120, 0.15)) !important;
  border: 1px solid rgba(200, 175, 140, 0.35) !important;
  box-shadow: 0 4px 16px rgba(170, 135, 120, 0.06);
  color: #786048;
}
body.dark-theme .weather-card.weather-effect-haze {
  background: linear-gradient(135deg, rgba(100, 85, 75, 0.22), rgba(70, 60, 55, 0.15)) !important;
  border-color: rgba(200, 175, 140, 0.18) !important;
  color: #a78bfa;
}
.weather-card.weather-effect-haze .weather-bg-effect {
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(255, 255, 255, 0) 100%);
  animation: haze-fog-drift 7s infinite linear;
}
@keyframes haze-fog-drift {
  0% { transform: translateX(-60%); }
  100% { transform: translateX(60%); }
}

.weather-icon-inline {
  color: #ff9800;
  font-size: 14px;
}

.weather-wind {
  font-size: 11px;
  opacity: 0.7;
  font-weight: normal;
}

body.dark-theme .weather-card {
  background: rgba(255, 152, 0, 0.15);
  border-color: rgba(255, 152, 0, 0.25);
  color: #ffffff;
}

body.dark-theme .weather-card:hover {
  background: rgba(255, 152, 0, 0.25);
  border-color: rgba(255, 152, 0, 0.45);
}

body.dark-theme .weather-icon-inline {
  color: #add8e6;
}

/* 气泡弹出框整体设计 */
.weather-detail-panel {
  padding: 4px;
  color: #333;
  font-family: system-ui, -apple-system, sans-serif;
}

.pop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.pop-location {
  font-weight: bold;
  font-size: 15px;
  color: #1d1d1f;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pop-location i {
  color: #f44336;
}

.pop-time {
  font-size: 11px;
  color: #8e8e93;
}

.pop-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.pop-temp-box {
  display: flex;
  align-items: baseline;
  position: relative;
}

.pop-temp-num {
  font-size: 38px;
  font-weight: 700;
  color: #1d1d1f;
  line-height: 1;
}

.pop-temp-unit {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  margin-left: 2px;
}

.pop-weather-txt {
  font-size: 16px;
  font-weight: bold;
  color: #ff9800;
  margin-left: 12px;
}

.pop-sub-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  width: 160px;
}

.sub-item {
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.03);
  padding: 6px 10px;
  border-radius: 8px;
}

.sub-label {
  font-size: 10px;
  color: #8e8e93;
}

.sub-val {
  font-size: 12px;
  font-weight: bold;
  color: #1d1d1f;
  margin-top: 2px;
}

/* AQI样式 */
.pop-aqi-box {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  padding: 10px;
  margin-bottom: 16px;
}

.aqi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.aqi-title {
  font-size: 12px;
  font-weight: bold;
  color: #8e8e93;
}

.aqi-badge {
  font-size: 11px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 10px;
  color: #fff;
}

.aqi-badge.level-1 { background-color: #4caf50; } /* 优 */
.aqi-badge.level-2 { background-color: #ffeb3b; color: #333; } /* 良 */
.aqi-badge.level-3 { background-color: #ff9800; } /* 轻度 */
.aqi-badge.level-4 { background-color: #f44336; } /* 中度 */
.aqi-badge.level-5 { background-color: #9c27b0; } /* 重度 */
.aqi-badge.level-6 { background-color: #795548; } /* 严重 */

.aqi-pollutants-single {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  margin-top: 4px;
}

.pm-label {
  color: #8e8e93;
}

.pm-val {
  font-weight: bold;
  color: #1d1d1f;
}

/* 预警 */
.pop-alerts {
  margin-bottom: 16px;
}

.alert-item {
  background: rgba(244, 67, 54, 0.08);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 8px;
  padding: 8px;
  color: #d32f2f;
  margin-bottom: 6px;
}

.alert-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: bold;
  font-size: 12px;
}

.alert-text {
  font-size: 11px;
  margin-top: 4px;
  line-height: 1.4;
}

/* 预报 */
.pop-forecast {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding-top: 12px;
  margin-bottom: 16px;
}

.forecast-title {
  font-size: 12px;
  font-weight: bold;
  color: #8e8e93;
  margin-bottom: 8px;
}

.forecast-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.forecast-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 4px 6px;
  background: rgba(0, 0, 0, 0.01);
  border-radius: 6px;
}

.fore-day {
  width: 60px;
  color: #333;
}

.fore-weather {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #555;
  flex: 1;
}

.fore-icon {
  color: #ff9800;
}

.fore-temp {
  font-weight: bold;
  color: #1d1d1f;
  width: 80px;
  text-align: right;
}

/* 生活指数 */
.pop-indices {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding-top: 12px;
}

.indices-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.index-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.index-icon {
  font-size: 16px;
  margin-top: 2px;
}

.index-body {
  display: flex;
  flex-direction: column;
}

.index-name {
  font-size: 11px;
  font-weight: bold;
  color: #333;
}

.index-desc {
  font-size: 10px;
  color: #8e8e93;
  margin-top: 1px;
}

/* 暗色模式气泡覆盖 */
body.dark-theme .weather-detail-panel {
  color: #e0e0e0;
}

body.dark-theme .pop-location {
  color: #ffffff;
}

body.dark-theme .pop-temp-num,
body.dark-theme .pop-temp-unit {
  color: #ffffff;
}

body.dark-theme .sub-item {
  background: rgba(255, 255, 255, 0.05);
}

body.dark-theme .sub-val {
  color: #ffffff;
}

body.dark-theme .pop-aqi-box {
  background: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.08);
}

body.dark-theme .poll-val {
  color: #ffffff;
}

body.dark-theme .forecast-item {
  background: rgba(255, 255, 255, 0.02);
}

body.dark-theme .fore-day,
body.dark-theme .fore-weather,
body.dark-theme .fore-temp,
body.dark-theme .index-name,
body.dark-theme .pm-val {
  color: #e0e0e0;
}

body.dark-theme .fore-icon {
  color: #add8e6;
}



.online-count {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  font-size: 12.5px;
  font-weight: 600;
  color: #4CAF50;
  background: none !important;
  border: none !important;
  padding: 0 !important;
  box-shadow: none !important;
  line-height: 1.2;
}

.online-count-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.online-count i {
  color: #4CAF50;
  font-size: 13px;
}

.count-text {
  font-weight: bold;
  color: #4CAF50;
  font-size: 12.5px;
}

.count-label {
  color: #1d1d1f;
  margin-left: 2px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}

.user-location {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #1d1d1f;
  background: none !important;
  border: none !important;
  padding: 0 !important;
  box-shadow: none !important;
  line-height: 1.2;
  width: 100%;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-location i {
  color: #1e80ff;
  font-size: 12px;
}

.location-text {
  color: #1d1d1f;
  font-weight: 600;
  font-size: 12.5px;
  white-space: nowrap;
}

.isp-text {
  color: #1d1d1f;
  font-size: 11px;
  font-weight: normal;
  opacity: 0.85;
  white-space: nowrap;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border-radius: 12px;
}

.connection-status .connected {
  color: #4CAF50;
  animation: pulse 2s infinite;
}

.connection-status .disconnected {
  color: #f44336;
}

.status-text {
  font-size: 13px;
  font-weight: 700;
  color: #1d1d1f;
  white-space: nowrap;
}

.connection-status .connected + .status-text {
  color: #4CAF50;
}

.connection-status .disconnected + .status-text {
  color: #f44336;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

/* 夜间模式下的在线信息样式 */
body.dark-theme .online-info {
  color: #e0e0e0;
}

body.dark-theme .count-label,
body.dark-theme .location-text,
body.dark-theme .isp-text {
  color: rgba(255, 255, 255, 0.85);
  text-shadow: none;
}

/* 夜间模式下的头部容器样式 */
body.dark-theme .header-container {
  background: rgba(30, 30, 30, 0.5);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.main-title {
  font-family: '爱点风雅黑', sans-serif;
  text-align: center;
  line-height: 100px;
  margin: 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  min-width: 320px; /* 稳定打字机过程，防止宽度抖动 */
  padding: 0 60px;  /* 扩大鼠标触发热区 */
  z-index: 10;
  cursor: pointer;
  user-select: none;
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;
  margin-left: auto;
  z-index: 2;
}

/* 天气卡片样式 */
.weather-container {
  display: flex;
  align-items: center;
}

.weather-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  color: #555;
  font-size: 14px;
}

.weather-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.15);
}

.weather-icon {
  font-size: 16px;
  color: #ff9800;
}

.weather-temp {
  font-weight: bold;
}

.weather-text {
  font-size: 12px;
  opacity: 0.8;
}

/* 暗色模式下天气卡片 */
body.dark-theme .weather-btn {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.2);
  color: #e0e0e0;
}

body.dark-theme .weather-btn:hover {
  background: rgba(0, 0, 0, 0.5);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

body.dark-theme .weather-icon {
  color: #add8e6;
}

@media screen and (max-width: 768px) {
  .weather-container {
    display: none !important;
  }
}

/* 夜间模式切换按钮样式 */
.theme-toggle-container {
  display: flex;
  align-items: center;
}

.theme-toggle-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  color: #ffc445;
  font-size: 18px;
}

.theme-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05) rotate(180deg);
  color: #ff9800;
  box-shadow: 0 4px 15px rgba(255, 196, 69, 0.3);
}

.theme-toggle-btn:active {
  transform: scale(1.02) rotate(180deg);
}

/* 夜间模式下的按钮样式 */
body.dark-theme .theme-toggle-btn {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.2);
  color: #87ceeb;
}

body.dark-theme .theme-toggle-btn:hover {
  background: rgba(0, 0, 0, 0.5);
  border-color: rgba(255, 255, 255, 0.4);
  color: #add8e6;
  box-shadow: 0 4px 15px rgba(135, 206, 235, 0.3);
}

.auth-buttons {
  display: flex;
  gap: 15px;
}

.auth-btn {
  padding: 10px 20px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.login-btn {
  background: rgba(255, 255, 255, 0.2);
  color: #333;
  border-color: rgba(255, 255, 255, 0.3);
}

.login-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.register-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.register-btn:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgba(0, 113, 227, 0.4);
}

/* 夜间模式下的登录注册按钮样式 */
body.dark-theme .login-btn {
  background: rgba(0, 0, 0, 0.3);
  color: #e0e0e0;
  border-color: rgba(255, 255, 255, 0.2);
}

body.dark-theme .login-btn:hover {
  background: rgba(0, 0, 0, 0.5);
}

body.dark-theme .register-btn {
  background: rgba(76, 175, 80, 0.8);
  color: #e0e0e0;
}

.user-info {
  position: relative;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.user-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-fallback-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-weight: bold;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  font-size: 16px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  user-select: none;
}

.user-menu {
  position: absolute;
  top: 50px;
  right: 0;
  background: rgba(255, 255, 255, 0.98); /* 调高不透明度 */
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  min-width: 150px;
  z-index: var(--z-dropdown);
  overflow: hidden;
}

/* 夜间模式下的用户菜单样式 */
body.dark-theme .user-menu {
  background: rgba(45, 45, 45, 0.95);
  box-shadow: 0 10px 30px rgba(255, 255, 255, 0.1);
}

.user-name {
  padding: 15px 20px 10px;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 14px;
  text-align: center;
}

/* 夜间模式下的用户名样式 */
body.dark-theme .user-name {
  color: rgba(255, 255, 255, 0.9);
  border-bottom-color: rgba(255, 255, 255, 0.2);
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #555;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 14px;
  text-align: center;
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}
.user-menu .menu-item {
  animation: fadeSlideIn 180ms ease both;
}
.user-menu .menu-item:nth-of-type(1) { animation-delay: 30ms; }
.user-menu .menu-item:nth-of-type(2) { animation-delay: 60ms; }
.user-menu .menu-item:nth-of-type(3) { animation-delay: 90ms; }

/* 夜间模式下的菜单项样式 */
body.dark-theme .menu-item {
  color: rgba(255, 255, 255, 0.8);
}

.menu-item:hover {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

/* 夜间模式下的菜单项悬停样式 */
body.dark-theme .menu-item:hover {
  background: rgba(102, 126, 234, 0.2);
  color: #87ceeb;
}

.menu-item i {
  margin-right: 8px;
  width: 16px;
}

.icon-user::before {
  content: '👤';
}

.icon-logout::before {
  content: '🚪';
}

@media (max-width: 768px) {
  .header-container {
    flex-direction: column;
    padding: 20px;
    gap: 20px;
  }
  
  .main-title {
    display: none;         /* 移动端隐藏“云语匀雨”标题 */
  }
  
  .online-info {
    width: 100%;
    justify-content: center;
    gap: 12px;
  }

  .status-group,
  .weather-card {
    flex: 1;
    min-width: 140px;
    max-width: 200px;
    min-height: 52px;
    height: auto;
  }
  
  .user-actions {
    margin-left: 0;
    justify-content: center;
  }
  
  .auth-buttons {
    gap: 10px;
  }
  
  .auth-btn {
    padding: 8px 16px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .header-container {
    margin: 10px;
    padding: 15px;
  }
  
  .main-title {
    display: none;         /* 小屏同样隐藏标题 */
  }
  
  .online-info {
    gap: 8px;
    width: 100%;
  }

  .status-group,
  .weather-card {
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
  }
  
  .user-actions {
    margin-left: 0;
    justify-content: center;
  }
  
  .auth-buttons {
    flex-direction: row;
    width: 100%;
    gap: 8px;
  }
  
  .auth-btn {
    text-align: center;
    flex: 1;
    padding: 10px 12px;
    font-size: 13px;
  }
}
.h1title {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), text-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  transform-origin: center;
  position: relative;
  will-change: transform, text-shadow;
}

.h1title.changing {
  transform: scale(1.05);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.dark-theme .h1title{
    color: white;
}

/* 打字机光标效果 */
.h1title.typing-caret::after {
  content: '|';
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  color: #4CAF50;
  animation: caretBlink 800ms steps(1, end) infinite;
  pointer-events: none;
}

@keyframes caretBlink {
  50% { opacity: 0; }
}
</style>

<style>
/* 全局 el-popover 弹出框样式重置，实现磨砂玻璃质感 */
.el-popover.weather-popover {
  background: rgba(255, 255, 255, 0.75) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(255, 255, 255, 0.4) !important;
  border-radius: 16px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
  padding: 14px !important;
}

body.dark-theme .el-popover.weather-popover {
  background: rgba(30, 30, 30, 0.75) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
}

/* 隐藏 element-plus 的自带小三角，避免样式折断 */
.el-popover.weather-popover .el-popper__arrow {
  display: none !important;
}

@media screen and (max-width: 480px) {
  .el-popover.weather-popover {
    max-width: 90vw !important;
    box-sizing: border-box;
  }
}
</style>